import { useState, useCallback } from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { QUpdatePlansRFC } from "@coreapp/CoreApp/QUpdatePlansRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { useToast, TOAST_STATUS } from "@/providers/ToastProvider";
import { appstr } from "@coreapp/utils/appstr";
import { isNullish } from "@theorgchartdev/storybook";
import { ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import { kAdminUser } from "@coreapp/constants";

/**
 * TODO: Migrate this file to TypeScript (.ts)
 * TODO: Refactor to use PlanManagerContext for plan and submission info instead of GetWSMgr/state library
 */

/**
 * Custom hook for managing plan submissions (submit, withdraw, approve, reject)
 * Handles the complete lifecycle of plan approval workflows
 */
export const usePlanSubmission = () => {
  const { addToast } = useToast();

  /**
   * Submit a plan for approval
   * @param {Array<{username: string, name: string}>} reviewers - Array of reviewer objects
   * @param {string} message - Submission message
   */

  const submitPlanSubmission = useCallback(
    (reviewers, message, nextPlanUsers) => {
      const wsMgr = GetWSMgr();
      if (!wsMgr) return;

      const planMgr = wsMgr.fPlanManager;
      if (!planMgr) return;

      const planInfo = planMgr.GetPlanInfo();
      if (isNullish(planInfo?.ID)) return;

      const requestData = {
        planID: planInfo.ID,
        reviewers: reviewers.map(user => ({ username: user.username })),
        message: message || "",
        newPlanUsers: nextPlanUsers
      };

      const callBackObj = {
        onResult: (success, response) => {
          if (success) {
            addToast({
              status: TOAST_STATUS.SUCCESS,
              message: appstr("Plan submitted for approval successfully")
            });
            planMgr.LoadPlanSubmission();
          } else {
            addToast({
              status: TOAST_STATUS.ERROR,
              message: response.message || appstr("Failed to submit plan submission")
            });
          }
        },
        fWSMgr: wsMgr,
        UpdatePanel: () => {}
      };

      const rfc = new QUpdatePlansRFC();
      rfc.InitUpdatePlans(callBackObj);
      rfc.SetParams(
        wsMgr.GetAccountName(),
        wsMgr.GetUserName(),
        "submitPlanSubmission",
        JSON.stringify(requestData)
      );

      Globals._rfcMgr.QueueRFC(rfc);
    },
    [addToast]
  );

  /**
   * Withdraw a plan submission
   * @param {string} message - Withdrawal message
   */
  const withdrawPlanSubmission = useCallback(
    message => {
      const wsMgr = GetWSMgr();
      if (!wsMgr) return;

      const planMgr = wsMgr.fPlanManager;
      if (!planMgr) return;

      const planInfo = planMgr.GetPlanInfo();
      if (isNullish(planInfo?.ID)) return;

      const requestData = {
        planID: planInfo.ID,
        message: message || ""
      };

      const callBackObj = {
        onResult: (success, response) => {
          if (success) {
            addToast({
              status: TOAST_STATUS.SUCCESS,
              message: appstr("Plan submission withdrawn successfully")
            });
            planMgr.LoadPlanSubmission();
          } else {
            addToast({
              status: TOAST_STATUS.ERROR,
              message: response.message || appstr("Failed to withdraw plan submission")
            });
          }
        },
        fWSMgr: wsMgr,
        UpdatePanel: () => {}
      };

      const rfc = new QUpdatePlansRFC();
      rfc.InitUpdatePlans(callBackObj);
      rfc.SetParams(
        wsMgr.GetAccountName(),
        wsMgr.GetUserName(),
        "withdrawPlanSubmission",
        JSON.stringify(requestData)
      );

      Globals._rfcMgr.QueueRFC(rfc);
    },
    [addToast]
  );

  const reviewPlanSubmission = useCallback(
    (status, feedback) => {
      const wsMgr = GetWSMgr();
      if (!wsMgr) return;

      const planMgr = wsMgr.fPlanManager;
      if (!planMgr) return;

      const planInfo = planMgr.GetPlanInfo();
      if (isNullish(planInfo?.ID)) return;

      const requestData = {
        planID: planInfo.ID,
        status,
        feedback: feedback || ""
      };

      const successMessages = {
        Approved: appstr("Plan approved successfully"),
        Rejected: appstr("Plan rejected")
      };

      const errorMessages = {
        Approved: appstr("Failed to approve plan"),
        Rejected: appstr("Failed to reject plan")
      };

      const callBackObj = {
        onResult: (success, response) => {
          if (success) {
            addToast({
              status: TOAST_STATUS.SUCCESS,
              message: successMessages[status]
            });
            planMgr.LoadPlanSubmission();
          } else {
            addToast({
              status: TOAST_STATUS.ERROR,
              message: response.message || errorMessages[status]
            });
          }
        },
        fWSMgr: wsMgr,
        UpdatePanel: () => {}
      };

      const rfc = new QUpdatePlansRFC();
      rfc.InitUpdatePlans(callBackObj);
      rfc.SetParams(
        wsMgr.GetAccountName(),
        wsMgr.GetUserName(),
        "reviewPlanSubmission",
        JSON.stringify(requestData)
      );

      Globals._rfcMgr.QueueRFC(rfc);
    },
    [addToast]
  );

  const approvePlanSubmission = useCallback(
    feedback => {
      reviewPlanSubmission("Approved", feedback);
    },
    [reviewPlanSubmission]
  );

  const rejectPlanSubmission = useCallback(
    feedback => {
      reviewPlanSubmission("Rejected", feedback);
    },
    [reviewPlanSubmission]
  );

  return {
    submitPlanSubmission,
    withdrawPlanSubmission,
    approvePlanSubmission,
    rejectPlanSubmission
  };
};
